import { z } from 'zod/v3';

/**
 * RFC-179 canonical schema: MFA_POLICY.
 *
 * Vendor-agnostic "MFA enforcement is configured at the IdP/account level"
 * fact. Covers Okta sign-on policies, M365 conditional access policies,
 * AWS account-level MFA, AAD MFA registration.
 *
 * Per-user MFA enrollment lives on IDENTITY_USER.isMfaEnrolled — this
 * canonical type is about policy/configuration, not enrollment status.
 */

export const MFA_POLICY_SCHEMA_VERSION = 1;

export const MfaPolicy = z.object({
  /** Human-friendly policy name. */
  name: z.string(),
  /** Provider-stable id. */
  externalId: z.string(),

  /** Policy is in effect (enabled / active). */
  enabled: z.boolean(),

  /** Whether MFA is *required* by this policy (vs optional / prompted). */
  requiresMfa: z.boolean(),

  /** Allowed MFA factor types (e.g. ['totp', 'webauthn']). Empty = unspecified. */
  allowedFactors: z.array(z.string()),

  /**
   * Population the policy applies to.
   * - 'all': applies to all users
   * - 'group': applies to specific groups (groupNames populated)
   * - 'role': applies to a specific role/scope (e.g. admins)
   * - 'unknown': provider didn't expose the assignment
   */
  appliesTo: z.enum(['all', 'group', 'role', 'unknown']),
  /** When appliesTo == 'group', the group names this policy covers. */
  groupNames: z.array(z.string()),

  /** Bypass / exception users (count, not the actual list — PII-minimal). */
  bypassUserCount: z.number().int().min(0),
});

export type MfaPolicy = z.infer<typeof MfaPolicy>;
